/**
 * Component can be used and behaves as standard html select, but has custom styles
 *
 *
 * @attr {String} [css-modifier] - Root element css class modifier(`select--${css-modifier}`)
 * @attr {String} [default-value] - Default value of select
 * @attr {String} [placeholder] - Select placeholder
 * @attr {Array} [options] - Array of select options({value: 'optionValue1', label: 'description for option'})
 *
 * <e2-custom-select
 *      .options="[{value: 'optionValue1', label: 'description for first option'}, {value: 'optionValue2', label: 'description for second option'}]"
 *      default-value="optionValue1"
 *      placeholder="Placeholder for select"
 * ></e2-custom-select>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { LightLitElement } from '@elab/components';

export class CustomSelect extends LightLitElement {
    static get properties() {
        return {
            cssModifier: { type: String, attribute: 'css-modifier' },
            defaultValue: { type: String, attribute: 'default-value' },
            tabIndexValue: { type: Number, attribute: 'tabindex-value' },
            placeholder: { type: String },
            options: { type: Array },
            selectedValue: { type: String, attribute: false },
            opened: { type: Boolean, attribute: false },
        };
    }

    constructor() {
        super();

        this.cssModifier = '';
        this.placeholder = '';
        this.options = [];
        this.opened = false;
        this.selectedValue = '';
        this.tabIndexValue = 0;
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (_changedProperties.has('defaultValue') || _changedProperties.has('options')) {
            if (this.defaultValue !== '') {
                this.selectedValue = this.defaultValue;
            } else if (this.options && this.options[0]) {
                this.selectedValue = this.options[0].value;
            }
        }
    }

    setSelectedValue(value) {
        this.selectedValue = value;
    }

    setStatus(status) {
        this.opened = status;
    }

    toggleStatus() {
        this.setStatus(!this.opened);
    }

    getSelectedOptionTemplate = () => {
        if (this.options.length === 1 && this.options[0].value !== this.selectedValue) {
            this.selectedValue = this.options[0].value;
        }

        const currOption = this.options.find(option => option.value === this.selectedValue) || {};
        const label = currOption.label || this.placeholder;
        const img = currOption.media;

        return html`
            <span class="select__selected-option">
                ${img
                    ? html`
                          <img src="${img}" class="select__option-image" />
                      `
                    : ''}
                ${label}
            </span>
        `;
    };

    handleSelect(value) {
        if (this.selectedValue !== value) {
            this.selectedValue = value;
            this.dispatchEvent(new CustomEvent('select', { detail: this.selectedValue }));
        }
        this.opened = false;
    }

    getOptionTemplate = ({ value, label, media }) => {
        const optionClasses = classMap({
            select__option: true,
            'select__option--selected': value === this.selectedValue,
        });

        return html`
            <li class="${optionClasses}" @click="${() => this.handleSelect(value)}">
                ${media
                    ? html`
                          <img src="${media}" class="select__option-image" />
                      `
                    : ''}
                <span>${label}</span>
            </li>
        `;
    };

    handleFocusSwitch(focused) {
        if (!focused) {
            this.setStatus(false);
        }
        const event = focused ? 'focused' : 'blured';
        this.dispatchEvent(new CustomEvent(event));
    }

    render() {
        const selectClasses = classMap({
            select: true,
            [`select--${this.cssModifier}`]: this.cssModifier,
            'select--open': this.opened,
        });

        return html`
            <v-slot name="label"></v-slot>
            <div
                class="${selectClasses}"
                tabindex="${this.tabIndexValue}"
                @blur="${() => this.handleFocusSwitch(false)}"
                @focus="${() => this.handleFocusSwitch(true)}"
            >
                <div class="select__wrapper" @click="${() => this.toggleStatus()}">
                    ${this.getSelectedOptionTemplate()}
                    <i class="select__arrow"></i>
                </div>
                ${this.opened
                    ? html`
                          <ul class="select__options">
                              ${this.options.map(this.getOptionTemplate)}
                          </ul>
                      `
                    : html``}
            </div>
        `;
    }
}

export default () => customElements.define('e2-custom-select', CustomSelect);
